// Generated from doskai-contracts/contracts/policies/operational-list-policy.ts. Do not edit.
import { adTransactionSearch, adTransactionTerms } from './ads-transaction-list-policy.generated';
/** Shared canonical predicates for private Investors, Payouts and Billing audit lists.
 * These are read models only; payout execution and authorization remain with their existing owners. */
// Writer-supported actions, not a claim about observed historical action counts.
export const BILLING_AUDIT_ACTIONS = [
  'stripe_price_ids_updated',
  'billing_secret_rotated',
  ...['reserve', 'request', 'approve', 'reject', 'execute', 'reconcile'].map(
    (action) => `company-withdrawal-${action}-attempt`
  ),
];
export type OperationalListDomain = 'investors' | 'payouts' | 'billingAudit' | 'studioProjects';
export function operationalListCriteria(input: any = {}) {
  const searchText = adTransactionSearch(input.searchText);
  const status = String(input.status || 'all')
    .trim()
    .toLowerCase();
  const sortMode = String(input.sortMode || input.sortBy || 'newest');
  if (!['newest', 'oldest', 'nameAsc', 'nameDesc'].includes(sortMode))
    throw new Error('Unsupported operational list sort');
  if (searchText.length > 64 || status.length > 60) throw new Error('Operational criteria too long');
  const currency = String(input.currency || '')
    .trim()
    .toUpperCase();
  if (currency && currency !== 'UNKNOWN' && !/^[A-Z]{3}$/.test(currency))
    throw new Error('Invalid operational currency');
  const date: { createdFrom?: number; createdBefore?: number } = {};
  for (const field of ['createdFrom', 'createdBefore'] as const) {
    if (input[field] == null) continue;
    if (!Number.isSafeInteger(input[field]) || input[field] < 0) throw new Error('Invalid operational creation range');
    date[field] = input[field];
  }
  if (date.createdFrom !== undefined && date.createdBefore !== undefined && date.createdFrom >= date.createdBefore)
    throw new Error('Invalid operational creation range');
  return { searchText, status, sortMode, ...date, ...(currency ? { currency } : {}) };
}
export function operationalListStatus(row: any, domain: OperationalListDomain) {
  if (domain === 'studioProjects') return row?.archived === true ? 'archived' : 'active';
  return domain === 'billingAudit'
    ? adTransactionSearch(row?.action)
    : String(row?.status || (domain === 'investors' ? 'new' : 'pending')).toLowerCase();
}
export function operationalListMatches(domain: OperationalListDomain, row: any, filters: any = {}) {
  const criteria = operationalListCriteria(filters);
  const status = operationalListStatus(row, domain);
  const matchesStatus =
    domain === 'billingAudit'
      ? criteria.status === 'all' || criteria.status === status
      : criteria.status === 'all' ||
        (criteria.status === 'marked'
          ? row?.mark === true
          : criteria.status === 'unread'
            ? !row?.read
            : status === criteria.status);
  const createdAt = operationalListOrderValue(row, { sortMode: 'newest' }) as number;
  const rawCurrency = String(row?.currencyCode || '')
    .trim()
    .toUpperCase();
  const currency = /^[A-Z]{3}$/.test(rawCurrency) ? rawCurrency : 'UNKNOWN';
  const matchesPayoutScope =
    domain !== 'payouts' ||
    (((criteria.createdFrom === undefined && criteria.createdBefore === undefined) || createdAt > 0) &&
      (criteria.createdFrom === undefined || createdAt >= criteria.createdFrom) &&
      (criteria.createdBefore === undefined || createdAt < criteria.createdBefore) &&
      (!criteria.currency || currency === criteria.currency));
  return matchesStatus && matchesPayoutScope && operationalListTerms(row, domain).includes(criteria.searchText);
}
export function operationalListTerms(row: any, domain: OperationalListDomain) {
  if (domain === 'studioProjects')
    return adTransactionTerms([
      row?.key,
      row?.id,
      row?.title,
      row?.name,
      row?.description,
      row?.platformType,
      row?.workflowStatus ?? row?.status,
      ...(Array.isArray(row?.tags) ? row.tags : []),
    ]);
  if (domain === 'billingAudit')
    return adTransactionTerms([
      row?.key,
      row?.action,
      row?.actorUid,
      row?.actorEmail,
      row?.reason,
      row?.environment,
      row?.projectId,
    ]);
  return adTransactionTerms([
    row?.key,
    row?.name,
    row?.email,
    row?.message,
    row?.casenumber,
    row?.caseNumber,
    row?.uid,
    row?.userId,
    row?.status || (domain === 'investors' ? 'new' : 'pending'),
    String(row?.fundingStatus || '').replace(/_/g, ' '),
  ]);
}
export function operationalListFacet(status: string, search = '') {
  return JSON.stringify([status === 'all' ? '' : status, adTransactionSearch(search)]);
}
export function operationalListOrderValue(row: any, filters: any = {}): number | string {
  const { sortMode } = operationalListCriteria(filters);
  if (sortMode === 'nameAsc' || sortMode === 'nameDesc') return adTransactionSearch(row?.name ?? row?.title);
  const value = row?.timeStamp ?? row?.updatedAt ?? row?.createdAt;
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : typeof value === 'string' && value.trim() && Number.isFinite(Number(value))
      ? Number(value)
      : typeof value === 'string' && Number.isFinite(Date.parse(value))
        ? Date.parse(value)
        : 0;
}
export function operationalListCompare(a: any, b: any, filters: any = {}) {
  const { sortMode } = operationalListCriteria(filters);
  const direction = sortMode === 'oldest' || sortMode === 'nameAsc' ? 1 : -1;
  const left = operationalListOrderValue(a, filters);
  const right = operationalListOrderValue(b, filters);
  const rank = (value: any) => (typeof value === 'number' ? 0 : 1);
  const order = rank(left) - rank(right) || (left < right ? -1 : left > right ? 1 : 0);
  return direction * (order || (String(a?.key) < String(b?.key) ? -1 : String(a?.key) > String(b?.key) ? 1 : 0));
}
/** Same explicit precedence as the established payout detail/execution adapter. Nullish zero is valid. */
export function payoutRequestedMinor(row: any) {
  return row?.requestedAmountMinor ?? row?.amountCents ?? row?.amount ?? row?.totalAmountCents;
}
